import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import wretch from "wretch";
import { UILanguageCode } from "../types";

type Session = {
  jwtToken: string;
  identity: Identity;
};

type Claims = {
  identity: Identity;
  exp: number;
};

export type Identity = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  language: UILanguageCode;
  job: string;
  msGraphJobTitle: string;
};

export type AuthContextType = {
  init: (redirect: boolean) => void;
  identity: Identity | null | undefined;
  jwtToken: string | null | undefined;
  permissions: {
    // canAccess: boolean;
    admin: {
      canManageProjects: boolean;
      canManageSystems: boolean;
      canManageUsers: boolean;
      canManageItems: boolean;
      canManageClients: boolean;
    };
  };
  isLoading: boolean;
  isExpired: boolean;
  logout: () => void;
  expire: () => void;
  avatarUrl?: string;
};

const AuthContext = createContext<AuthContextType>(undefined!);

const getExpirationTime = (session: Session): number => {
  const claims: Claims = JSON.parse(atob(session.jwtToken.split(".")[1] ?? ""));
  return claims.exp * 1000; // Convert to milliseconds
};

// Function to check if JWT is expired
const isSessionValid = (session?: Session): boolean => {
  if (!session) return false; // Token doesn't exist
  return Date.now() < getExpirationTime(session); // Check if token is not expired
};

export interface RothoSSOSettings {
  clientId: string;
  iamUrl: string;
}

type AuthProviderProps = PropsWithChildren<{
  settings: RothoSSOSettings;
}>;

const AuthProvider: FC<AuthProviderProps> = ({ children, settings }: AuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const avatarObjectUrlRef = useRef<string | null>(null);

  const [permissions, setPermissions] = useState<string[]>();

  const logout = useCallback(() => {
    window.location.href = `${settings.iamUrl}/logout?redirect_uri=${window.location.href}`;

    // TODO-Fix
    // https://stackoverflow.com/questions/79042215/manage-cookies-on-localhost/79042309

    // wretch()
    //   // .options({ credentials: "include" }) // Ensure cookies are included in the request
    //   .url(`${accountsUrl}/api/v1/logout`)
    //   .get()
    //   .res()
    //   .then(() => {
    //     // remove the session from the state
    //     setSession(null);
    //     setIsLoading(false);

    //     // invalidate swr cache
    //     mutate((_) => true, undefined, { revalidate: false });

    //     // clear the timeout if set
    //     logoutTimeout.current && clearTimeout(logoutTimeout.current);
    //     setSession(null);
    //   });
  }, [settings.iamUrl]);

  const expire = useCallback(() => setIsExpired(true), []);

  const handleTokenExpiry = () => {
    setIsExpired(true);
    setIsLoading(false);
  };
  const clearAvatar = () => {
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
      avatarObjectUrlRef.current = null;
    }
    setAvatarUrl(undefined);
  };

  const reloadAvatar = () =>
    wretch()
      .options({ credentials: "include" }) // Ensure cookies are included in the request
      .url(`${settings.iamUrl}/api/v1/me/picture`)
      .get()
      .blob((blob) => {
        const url = URL.createObjectURL(blob);
        clearAvatar();
        avatarObjectUrlRef.current = url;
        setAvatarUrl(url);
      })
      .catch(console.log);

  const loadPermissions = (session: Session) => {
    return wretch()
      .options({ credentials: "include" }) // Ensure cookies are included in the request
      .auth(`Bearer ${session.jwtToken}`)
      .url("/api/me/permissions")
      .get()
      .json<string[]>()
      .then(setPermissions)
      .catch(console.log)
      .finally(() => setIsLoading(false));
  };

  const whoAmI = (redirect: boolean) =>
    wretch()
      .options({ credentials: "include" }) // Ensure cookies are included in the request
      .url(`${settings.iamUrl}/api/v1/sessions/whoami`)
      .get()
      .json<Session>()
      .then(setSession)
      .catch((err) => {
        // catch only 401 error
        if (redirect && err?.status === 401 && isLoading) {
          const params = new URLSearchParams(window.location.search);
          const redirect = params.get("redirect_uri");
          const uri = encodeURIComponent(redirect ? redirect : window.location.href);
          window.location.href = `${settings.iamUrl}?redirect_uri=${uri}&client_id=${settings.clientId}`;
        }
      });

  // useEffect(() => {
  //   whoAmI();
  // }, []);

  useEffect(() => {
    if (session?.jwtToken) {
      if (isSessionValid(session)) {
        loadPermissions(session);
        reloadAvatar();
      } else {
        handleTokenExpiry();
      }
      // 2024-10-21 Removed: set loading to loaded is made after permissions check
      // setIsLoading(!session.jwtToken);
    }
  }, [session?.jwtToken]);

  const hasPermission = (permission: string) => permissions?.includes(permission) ?? false;

  const contextValue: AuthContextType = {
    identity: session?.identity,
    jwtToken: session?.jwtToken,
    permissions: {
      // canAccess: !permissions?.[0],
      admin: {
        canManageProjects: hasPermission("projects:all"),
        canManageSystems: hasPermission("systems:all"),
        canManageUsers: hasPermission("users:all"),
        canManageItems: hasPermission("items:all"),
        canManageClients: hasPermission("clients:all"),
      },
    },
    isExpired,
    isLoading,
    logout,
    init: (redirect: boolean) => whoAmI(redirect),
    expire,
    avatarUrl,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
